/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '../api/auth';
import type { User, LoginRequest } from '../types/auth';
import { isAxiosError } from 'axios';

const decodeJwtExpiration = (token: string): number | null => {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const decoded = JSON.parse(atob(padded)) as { exp?: number };
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
};

const isTokenExpiredOrNearExpiry = (token: string, skewSeconds: number = 30): boolean => {
  const exp = decodeJwtExpiration(token);
  if (!exp) return true;
  return exp <= Math.floor(Date.now() / 1000) + skewSeconds;
};

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    const token = localStorage.getItem('access_token');

    // NÃO restaurar do cache - sempre validar com o backend primeiro
    const fetchUser = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        if (isTokenExpiredOrNearExpiry(token)) {
          const refreshed = await tryRefreshToken();
          if (!refreshed) {
            authApi.logout();
            queryClient.clear();
            setUser(null);
            window.dispatchEvent(new CustomEvent('session-expired'));
            return;
          }
        }

        const userInfo = await authApi.me();
        localStorage.setItem('user', JSON.stringify(userInfo));
        setUser(userInfo);
      } catch (error: unknown) {
        console.error('Erro ao recuperar sessão', error);

        // Se token expirado (401), tentar refresh
        if (isAxiosError(error) && error.response?.status === 401) {
          const refreshed = await tryRefreshToken();
          if (refreshed) {
            // Token renovado, tentar buscar usuário novamente
            try {
              const userInfo = await authApi.me();
              localStorage.setItem('user', JSON.stringify(userInfo));
              setUser(userInfo);
              return;
            } catch {
              // Falhou mesmo após refresh
            }
          }
        }

        // Não conseguiu recuperar sessão - limpar e notificar
        authApi.logout();
        queryClient.clear();
        setUser(null);
        window.dispatchEvent(new CustomEvent('session-expired'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [queryClient]);

  // Tenta renovar o access token usando o refresh token
  const tryRefreshToken = async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    try {
      const response = await authApi.refresh(refreshToken);
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      return true;
    } catch (error: unknown) {
      console.error('Erro ao renovar token', error);
      return false;
    }
  };

  const login = async (data: LoginRequest) => {
    const tokenResponse = await authApi.login(data);
    localStorage.setItem('access_token', tokenResponse.access_token);
    // Armazenar refresh token também
    if (tokenResponse.refresh_token) {
      localStorage.setItem('refresh_token', tokenResponse.refresh_token);
    }

    // Limpa cache do tenant anterior antes de carregar dados do novo tenant
    queryClient.clear();

    // Fetch user info with the new token
    const userInfo = await authApi.me();
    localStorage.setItem('user', JSON.stringify(userInfo));
    setUser(userInfo);
  };

  const logout = () => {
    authApi.logout();
    sessionStorage.removeItem('active_empresa_id');
    // Limpa todo o cache do React Query para não vazar dados entre tenants
    queryClient.clear();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
